import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, User, MapPin, Hash, ArrowRight } from "lucide-react";
import api from "../lib/api";

/**
 * Guest User Lead Capture Modal — Step 3 (live backend).
 * POSTs to /api/guest/lead → round-robin vendor assignment.
 * On success, navigates the user to the marriage services vendor directory.
 */
export default function GuestLeadModal({ open, onClose }) {
  const nav = useNavigate();
  const [form, setForm] = useState({ mobile: "", city: "", pin: "" });
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/guest/lead", form);
      toast.success("Fetching vendors in your area…");
      const params = new URLSearchParams({ lead_id: data.lead_id, city: form.city });
      onClose();
      nav(`/vendors?${params.toString()}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  const closeAll = () => { setForm({ mobile: "", city: "", pin: "" }); onClose(); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="guest-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(26,20,16,0.72)" }}>
      <div className="relative w-full max-w-md" style={{ background: "var(--ivory)", border: "1px solid var(--gold)" }}>
        <button data-testid="guest-modal-close" onClick={closeAll} aria-label="Close"
          className="absolute right-3 top-3 p-2" style={{ color: "var(--ash)" }}>
          <X size={18}/>
        </button>

        <div className="p-7 md:p-8">
          <div className="eyebrow">Guest · Free</div>
          <h3 className="font-serif text-2xl md:text-3xl mt-2" style={{ color: "var(--crimson)" }}>
            Find a Wedding Vendor
          </h3>
          <p className="mt-2 text-sm" style={{ color: "var(--ash)" }}>
            Share your location — we&apos;ll route you to a trusted local vendor.
          </p>

          {(
            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div>
                <label className="input-label">Mobile Number</label>
                <div className="flex items-center gap-2">
                  <User size={16} style={{ color: "var(--crimson)" }}/>
                  <input data-testid="guest-mobile" required type="tel" pattern="[0-9]{10}"
                    value={form.mobile} onChange={set("mobile")}
                    placeholder="10-digit mobile" className="underline-input"/>
                </div>
              </div>
              <div>
                <label className="input-label">City / Location</label>
                <div className="flex items-center gap-2">
                  <MapPin size={16} style={{ color: "var(--crimson)" }}/>
                  <input data-testid="guest-city" required
                    value={form.city} onChange={set("city")}
                    placeholder="e.g., Bettiah" className="underline-input"/>
                </div>
              </div>
              <div>
                <label className="input-label">Area PIN Code</label>
                <div className="flex items-center gap-2">
                  <Hash size={16} style={{ color: "var(--crimson)" }}/>
                  <input data-testid="guest-pin" required type="tel" pattern="[0-9]{6}"
                    value={form.pin} onChange={set("pin")}
                    placeholder="6-digit PIN" className="underline-input"/>
                </div>
              </div>

              <button data-testid="guest-submit" type="submit" disabled={busy} className="btn-primary w-full justify-center">
                {busy ? "Connecting…" : "Connect Me"} <ArrowRight size={16}/>
              </button>
              <p className="text-[10px] tracking-widest uppercase text-center" style={{ color: "var(--ash)" }}>
                Free · No signup required
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
